/**
 * Authoritative online/offline detection backed by a lightweight server ping, because
 * `navigator.onLine` is unreliable: when the Service Worker serves a page entirely from cache, no
 * network request reaches the browser, so `navigator.onLine` can stay `true` even with no
 * connectivity — e.g. reloading an offline tab whose shell is precached. The steady-state value then
 * lies, and the `offline`/`online` window events only fire on *transitions*, not on a fresh load.
 *
 * This monitor periodically fetches a tiny no-store endpoint ({@link PING_URL}) that the Service
 * Worker treats as network-only, so a failed fetch genuinely means the network is unreachable. It
 * polls faster while offline (to notice recovery) and slower while online (to notice a silent drop),
 * re-checks immediately on the browser's `online`/`offline` events, and re-checks when the tab becomes
 * visible. Subscribers are notified only when the verified state actually changes.
 *
 * Kept self-contained and reset-able so it's straightforward to unit test.
 */

/** Network-only probe (see sw-strategy `/api/` → 'network-only'); never served from cache. */
const PING_URL = '/api/ping';
/** Where the Service Worker records the last navigation's online/offline outcome (see sw.ts). */
const NAV_STATE_URL = '/__nav-state';
/** Ignore a nav-state seed older than this — it predates the current page load and may be stale. */
const NAV_STATE_MAX_AGE_MS = 60_000;
/** Poll cadence while believed online — just enough to catch a silent drop. */
const ONLINE_POLL_MS = 30_000;
/** Poll cadence while offline — frequent so reconnection is noticed promptly. */
const OFFLINE_POLL_MS = 5_000;
/** Abort a hung ping after this long and treat it as offline. */
const PING_TIMEOUT_MS = 5_000;

type Listener = (online: boolean) => void;

let online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
let started = false;
let checking = false;
/** Bumped on reset/stop so an in-flight ping from a previous cycle can't apply a stale result. */
let generation = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<Listener>();

/** The current best-known connectivity state (verified by ping once monitoring has started). */
export function isOnline(): boolean {
  return online;
}

/** True when the browser is (verified to be) offline. */
export function isOffline(): boolean {
  return !online;
}

/**
 * Subscribes to verified connectivity changes; the listener is called with the new online state
 * whenever it changes. Starts monitoring on first subscription and stops when the last unsubscribes.
 * Returns an unsubscribe function.
 */
export function onConnectivityChange(listener: Listener): () => void {
  listeners.add(listener);
  start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stop();
    }
  };
}

function setOnline(next: boolean): void {
  if (next === online) {
    return;
  }
  online = next;
  for (const listener of listeners) {
    listener(online);
  }
}

/** Performs a single ping; resolves true if the server is reachable, false otherwise. */
async function ping(): Promise<boolean> {
  if (typeof fetch === 'undefined') {
    return online;
  }
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timeout = controller ? setTimeout(() => controller.abort(), PING_TIMEOUT_MS) : undefined;
  try {
    const response = await fetch(PING_URL, { method: 'GET', cache: 'no-store', signal: controller?.signal });
    return response.ok || response.status === 204;
  } catch {
    return false;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

/**
 * Reads the Service Worker's record of the current page load's connectivity (online if the document
 * was served from the network, offline if from cache). This is a fast, network-free seed for the
 * initial state — the authoritative ping then confirms it. Returns undefined if absent/stale/unreadable.
 */
async function readNavStateSeed(): Promise<boolean | undefined> {
  if (typeof caches === 'undefined') {
    return undefined;
  }
  try {
    const response = await caches.match(NAV_STATE_URL);
    if (!response) {
      return undefined;
    }
    const data = (await response.json()) as { online?: unknown; at?: unknown };
    if (typeof data.online !== 'boolean') {
      return undefined;
    }
    // Only trust a marker recent enough to describe the current load, not a leftover from a prior one.
    if (typeof data.at === 'number' && Date.now() - data.at > NAV_STATE_MAX_AGE_MS) {
      return undefined;
    }
    return data.online;
  } catch {
    return undefined;
  }
}

/**
 * Reports how THIS page load was served, per the Service Worker's navigation record: `true` if the
 * document came from the network (online), `false` if the navigation fell back to the cache (offline),
 * or `undefined` when no recent marker exists. Unlike {@link checkConnectivityNow}, this reflects the
 * actual navigation outcome with no network round-trip, so it's well-suited to a first-paint decision
 * — e.g. the offline game page deciding, without a ping, whether it was reached online or offline.
 */
export function navigationLoadedOnline(): Promise<boolean | undefined> {
  return readNavStateSeed();
}

/** Runs a connectivity check now and updates the verified state. Safe to call concurrently (debounced). */
export async function checkConnectivityNow(): Promise<boolean> {
  if (checking) {
    return online;
  }
  checking = true;
  const gen = generation;
  try {
    const reachable = await ping();
    if (gen === generation) {
      setOnline(reachable);
    }
    return reachable;
  } finally {
    checking = false;
  }
}

function scheduleNext(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (timer) {
    clearTimeout(timer);
  }
  const delay = online ? ONLINE_POLL_MS : OFFLINE_POLL_MS;
  timer = setTimeout(() => {
    void checkConnectivityNow().finally(scheduleNext);
  }, delay);
}

const onNavOffline = (): void => {
  // A fired 'offline' event is trustworthy: act on it immediately, then poll for recovery.
  setOnline(false);
  scheduleNext();
};

const onNavOnline = (): void => {
  // Don't trust a fired 'online' event blindly (captive portals, server down): confirm with a ping.
  void checkConnectivityNow().finally(scheduleNext);
};

const onVisible = (): void => {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    void checkConnectivityNow().finally(scheduleNext);
  }
};

function start(): void {
  if (started || typeof window === 'undefined') {
    return;
  }
  started = true;
  const gen = generation;
  window.addEventListener('online', onNavOnline);
  window.addEventListener('offline', onNavOffline);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }
  // Seed from the Service Worker's record of THIS document load (fast, network-free) so a cached
  // offline reload is reflected immediately, then confirm/maintain with the authoritative ping.
  void (async () => {
    const seed = await readNavStateSeed();
    if (seed !== undefined && gen === generation) {
      setOnline(seed);
    }
    await checkConnectivityNow();
    scheduleNext();
  })();
}

function stop(): void {
  if (!started) {
    return;
  }
  started = false;
  generation++; // discard any in-flight ping result
  if (timer) {
    clearTimeout(timer);
    timer = undefined;
  }
  window.removeEventListener('online', onNavOnline);
  window.removeEventListener('offline', onNavOffline);
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisible);
  }
}

/** Test-only: tears down monitoring and re-derives the initial state from navigator.onLine. */
export function _resetConnectivityForTests(): void {
  stop();
  generation++;
  listeners.clear();
  checking = false;
  online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}
